import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AppShell from '../../components/AppShell';
import AdminTabs from '../../components/AdminTabs';
import { supabase } from '../../lib/supabase';
import type { Listing, Status } from '@viet-parenting/supabase/types';

type Row = Pick<
  Listing,
  'id' | 'name_vi' | 'slug' | 'status' | 'venue_city' | 'venue_country' | 'formats' | 'featured' | 'created_at' | 'updated_at'
>;

interface State {
  loading: boolean;
  rows: Row[];
  error: string | null;
}

const STATUS_FILTERS: ReadonlyArray<{ value: Status | 'all'; label: string }> = [
  { value: 'all', label: 'Tất cả' },
  { value: 'pending', label: 'Pending' },
  { value: 'verified', label: 'Active' },
];

const STATUS_LABEL: Record<Status, string> = {
  pending: 'Pending',
  verified: 'Active',
};

const STATUS_PILL: Record<Status, string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-100',
  verified: 'bg-emerald-50 text-emerald-700 border-emerald-100',
};

export default function AdminListings() {
  const [state, setState] = useState<State>({ loading: true, rows: [], error: null });
  const [filter, setFilter] = useState<Status | 'all'>('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      const { data, error } = await supabase
        .from('listings')
        .select('id, name_vi, slug, status, venue_city, venue_country, formats, featured, created_at, updated_at')
        .order('updated_at', { ascending: false });
      if (!active) return;
      setState({
        loading: false,
        rows: (data ?? []) as unknown as Row[],
        error: error?.message ?? null,
      });
    })();
    return () => {
      active = false;
    };
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return state.rows.filter((r) => {
      if (filter !== 'all' && r.status !== filter) return false;
      if (!q) return true;
      const haystack = [r.name_vi, r.slug, r.venue_city ?? '', r.venue_country ?? '']
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [state.rows, filter, query]);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <AdminTabs active="listings" />

        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl font-extrabold text-red-700 mb-1">Tất cả lớp học</h1>
            <p className="text-sm text-gray-500">Quản lý và cập nhật danh sách lớp học.</p>
          </div>
          <div className="flex items-center gap-3">
            <p className="text-xs text-gray-400">
              {state.loading ? '…' : `${visible.length} / ${state.rows.length}`}
            </p>
            <Link
              to="/admin/listings/new"
              className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              <span className="text-base leading-none">+</span>
              Tạo lớp mới
            </Link>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="flex flex-wrap gap-1">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setFilter(f.value)}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  filter === f.value
                    ? 'bg-red-50 border-red-200 text-red-700'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-red-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Tìm theo tên, slug, thành phố…"
            className="ml-auto w-64 max-w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>

        {state.error && (
          <div className="bg-red-50 text-red-700 text-sm rounded-xl px-5 py-4 mb-6 border border-red-100">
            {state.error}
          </div>
        )}

        {state.loading ? (
          <p className="text-sm text-gray-400">Đang tải…</p>
        ) : visible.length === 0 ? (
          <p className="text-sm text-gray-400">Không có lớp học nào khớp.</p>
        ) : (
          <ul className="brand-card divide-y divide-[rgba(109,200,181,0.25)] overflow-hidden">
            {visible.map((row) => (
              <li key={row.id}>
                <Link
                  to={`/admin/listings/${row.id}`}
                  className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {row.featured && (
                        <span
                          title="Featured"
                          className="text-amber-500 text-sm leading-none shrink-0"
                          aria-label="Featured"
                        >
                          ★
                        </span>
                      )}
                      <p className="font-medium text-gray-900 truncate">{row.name_vi}</p>
                      <span
                        className={`text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded border ${STATUS_PILL[row.status]}`}
                      >
                        {STATUS_LABEL[row.status]}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 truncate">
                      {row.venue_city ? `${row.venue_city}` : '—'}
                      {row.venue_country ? `, ${row.venue_country}` : ''}
                      {row.formats.length > 0 ? ` · ${row.formats.join(' / ')}` : ''}
                    </p>
                  </div>
                  <div className="text-xs text-gray-400 shrink-0 flex items-center gap-3">
                    <span>{new Date(row.updated_at).toLocaleDateString('vi-VN')}</span>
                    <span>→</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AppShell>
  );
}
